import Big from 'big.js';
import { DateTime } from 'luxon';
import { Start, Stop } from 'smg/buttons';
import * as contact from 'smg/customer-api/contact';
import { GetPrices } from 'smg/customer-api/lineitems';
import * as smgPromo from 'smg/customer-api/promos';
import * as quoterequests from 'smg/customer-api/quoterequests';
import { Session } from 'smg/customer-api/session';
import * as html from 'smg/html';
import Modal from 'smg/modal';
import * as models from 'smg/models';
import { CleanNumber } from 'smg/number';
import * as phone from 'smg/phone';
import { Phone } from 'smg/phone';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import Dropup from './components/dropup';
import { Uploader } from './components/uploader';
import { ModalOrOffcanvas } from './modal';

declare interface myWindow extends Window {
    dataLayer: Record<string, unknown>[];
}
declare const window: myWindow;

declare interface promptQuantityChangeOptions {
    quote?: models.Quote;
    order?: models.Order;
    lineItem: models.QuoteLineItem | models.OrderLineItem;
}

export async function PromptQuantityChange(opts: promptQuantityChangeOptions): Promise<void> {
    const body = template.Clone('#custom-qty-modal');
    const fullName = body.querySelector('#modal-full-name') as HTMLInputElement;
    const email = body.querySelector('#modal-email') as HTMLInputElement;
    const phone = body.querySelector('#modal-phone') as HTMLInputElement;
    const quantity = body.querySelector('#modal-quantity') as HTMLInputElement;
    const message = body.querySelector('#modal-message') as HTMLTextAreaElement;

    const p = new Phone(phone);

    const sess = await Session().catch(() => undefined);
    if (sess) {
        fullName.value = `${sess.firstName} ${sess.lastName}`;
        email.value = sess.email;
        void p.Iti.promise.then(() =>
            p.Iti.setNumber(sess.phone));
    }

    if (opts.lineItem.lineItemModel?.name === 'Patch') {
        quantity.min = '100';
    }

    let quoteLineItemIndex: number = 0;
    let orderLineItemIndex: number = 0;
    if (opts.quote?.quoteLineItems) {
        quoteLineItemIndex = opts.quote.quoteLineItems.indexOf(opts.lineItem) + 1;
    } else if (opts.order?.orderLineItems) {
        orderLineItemIndex = opts.order.orderLineItems?.indexOf(opts.lineItem) + 1;
    }

    const $reCaptchaPlaceholder = body.querySelector('#modal-recaptcha') as HTMLElement;

    return new Promise(resolve => new Modal({
        title: 'Change Quantity Request',
        body,
        size: 'modal-sm',
        headerLevel: 'h2',
        confirmButtonClass: 'btn btn-primary m-0',
        cancelButtonClass: 'btn btn-outline-primary m-0',
        appendBodyClass: 'p-4 px-md-20',
        appendClassBySelector: {
            '.modal-footer': 'justify-content-center flex-row-reverse gap-4',
            '.modal-body': 'change-quantity-request',
        },
        onSubmit: async () => {
            const postMessage = `Qty update request for ${opts.quote ? 'quote' : 'order'} #${opts.quote ? opts.quote.serial || '' : opts.order?.invoiceNumber || ''}

            New quantity requested: ${quantity.value}
            For line item #${opts.quote?.quoteLineItems ? quoteLineItemIndex : orderLineItemIndex}

            Other comments:
            ${message.value.trim() || 'None'}`;

            return contact.CreateContact({
                name: fullName.value,
                company: '',
                phone: p,
                email: email.value,
                content: postMessage,
                ignoreSMSPreference: true,
                $reCaptchaPlaceholder,
            }).then(() => resolve());
        },
    }));
}

export interface QuoteModalOptions {
    lineItem: string;
    type: string;
    size: string;
    quantity?: number;
    prefixNotes?: string;
    notesDefault?: string;
    children?: quoterequests.Child[];
    extras?: quoterequests.Extra[];

    form: string;

    backButtonText: string;
    disableCompany?: boolean;
}

export function QuoteModal(opts: QuoteModalOptions) {
    const $modalQuoteFormTemplate = document.querySelector('#modal-quote-form-template') as HTMLTemplateElement;

    const $body = template.Clone($modalQuoteFormTemplate).firstElementChild as HTMLElement;

    const $firstName = $body.querySelector('#modal-first-name') as HTMLInputElement;
    const $lastName = $body.querySelector('#modal-last-name') as HTMLInputElement;
    const $company = $body.querySelector('#modal-company') as HTMLInputElement;
    const $email = $body.querySelector('#modal-email') as HTMLInputElement;

    const $phone = $body.querySelector('#modal-phone') as HTMLInputElement;
    const p = new phone.Phone($phone);

    const $promo = $body.querySelector('#modal-promo') as HTMLInputElement;
    const $promoApply = $body.querySelector('#modal-promo-apply') as HTMLButtonElement;
    const $promoPreview = $body.querySelector('#modal-promo-preview') as HTMLElement;

    const promoInput = new smgPromo.Input($promo, {
        success: p => {
            $promoPreview.classList.remove('d-none');
            $promoPreview.innerHTML = `<b>${html.Escape(p.code)}:</b> ${html.Escape(p.description)}`;
        },
        error: () => {
            $promoPreview.classList.add('d-none');
            $promoPreview.innerHTML = '';
        },
    });

    $promoApply.addEventListener('click', async () => {
        promoInput.Dirty = true;
        Start($promoApply);
        await promoInput.Check().then(() => {
            Stop($promoApply);
        }).catch(() => {
            Stop($promoApply);
        });
    });

    // make sure the promo is checked if it's loaded from the url param
    // and run the success/error callbacks
    setTimeout(async () => {
        promoInput.Dirty = true;
        await promoInput.Check();
    });

    if (opts.disableCompany) {
        $body.querySelector('#modal-company-container')?.remove();
    }

    void Session().then(sess => {
        $firstName.value = sess.firstName;
        $lastName.value = sess.lastName;
        $email.value = sess.email;
        void p.Iti.promise.then(() =>
            p.Iti.setNumber(sess.phone));
    }).catch(() => undefined);

    const $submit = $body.querySelector('#modal-submit-button') as HTMLButtonElement;

    const $attachments = $body.querySelector('#modal-attachments') as HTMLInputElement;
    const $attachmentsLabel = $attachments.closest('.uploader-dropzone') as HTMLLabelElement;
    const attachments = new Uploader($attachmentsLabel, {
        confirmBtn: $submit,
    });

    const $cancel = $body.querySelector('#modal-cancel-button') as HTMLButtonElement;
    $cancel.addEventListener('click', () => {
        proofModal.Close();
    });

    const $quantity = $body.querySelector('#modal-quantity') as HTMLSelectElement;
    const $customQuantityContainer = $body.querySelector('#modal-custom-quantity-container') as HTMLElement;
    const $customQuantity = $body.querySelector('#modal-custom-quantity') as HTMLInputElement;

    const productName = (name: string) => {
        switch (name) {
        case 'Challenge Coins':
            return 'coin';
        case 'Lapel Pins':
            return 'pin';
        case 'Patches':
            return 'patch';
        case 'Key Chains':
        case 'Keychains':
            return 'keychain';
        case 'Lanyards':
            return 'lanyard';
        case 'Medals':
            return 'medal';
        case 'Belt Buckles':
            return 'buckle';
        case '':
            return 'product';
        default:
            return name.toLocaleLowerCase();
        }
    };

    if (!opts.quantity) {
        void GetPrices(String(opts.lineItem), String(opts.type), 0, undefined, undefined, String(opts.size)).then(prices => {
            if (!prices.length) {
                prices = [
                    { lineItem: '', type: '', size: '', quantity: 100, price: Big(0) },
                    { lineItem: '', type: '', size: '', quantity: 200, price: Big(0) },
                    { lineItem: '', type: '', size: '', quantity: 300, price: Big(0) },
                    { lineItem: '', type: '', size: '', quantity: 500, price: Big(0) },
                    { lineItem: '', type: '', size: '', quantity: 2_000, price: Big(0) },
                    { lineItem: '', type: '', size: '', quantity: 5_000, price: Big(0) },
                    { lineItem: '', type: '', size: '', quantity: 10_000, price: Big(0) },
                ];
            }

            const $radios = html.FromString(`
            <div class="d-flex flex-column px-3 gap-3">
            </div>`);

            const appendRadio = (text: string, value: string, price: Big, saveText: string) => {
                const $radio = html.FromString(`
                <div class="form-check my-0 py-2 w-100 d-flex gap-2 align-items-center">
                    <input class="form-check-input" type="radio" name="dropup-qty-radio" id="qty-radio-${value}" />
                    <label class="form-check-label flex-grow-1 d-flex gap-2 align-items-center" for="qty-radio-${value}">
                        <b class="flex-grow-1">${html.Escape(text)}</b>

                        ${!price.eq(0) ? `<div>
                            ${DollarFormat(price)} per ${productName(String(opts.lineItem))}
                        </div>
                        <hr class="vr my-0" ${!saveText ? 'style="visibility:hidden"' : ''}>
                        <div class="text-end modal-save-text">
                            ${saveText}
                        </div>` : ''}
                    </label>
                </div>`);
                $radios.appendChild($radio);

                const $radioInput = $radio.querySelector('input') as HTMLInputElement;
                $radioInput.checked = value === $quantity.value;
                $radioInput.addEventListener('change', () => {
                    $quantity.value = value;
                    $quantity.dispatchEvent(new Event('change'));

                    $quantityDropdownMenu.classList.remove('show');
                });
            };

            for (const price of prices) {
                let saveText = '';
                if (price !== prices[0] && !prices[0].price.eq(0)) {
                    // percent off the first price
                    saveText = `SAVE ${Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(prices[0].price.minus(price.price).div(prices[0].price).times(100).toNumber())}%`;
                }

                appendRadio(Intl.NumberFormat().format(price.quantity), price.quantity.toString(), price.price, saveText);
            }

            appendRadio('Custom', 'custom', Big(0), '');

            $quantity.appendChild(html.FromString('<option value="custom">Custom</option>'));
            $quantity.addEventListener('change', () => {
                qtyDropup?.Close();

                if ($quantity.value === 'custom') {
                    $customQuantityContainer.classList.remove('d-none');
                    $customQuantity.removeAttribute('disabled');
                    $customQuantity.focus();
                } else {
                    $customQuantityContainer.classList.add('d-none');
                    $customQuantity.setAttribute('disabled', '');
                }
            });

            const $quantityDropdownToggle = $body.querySelector('#modal-quantity-dropdown-toggle') as HTMLElement;
            if (window.window.innerWidth < 768) {
                $quantityDropdownToggle.removeAttribute('data-bs-toggle');
            }

            const $quantityDropdownMenu = $body.querySelector('#modal-quantity-dropdown-toggle + .dropdown-menu') as HTMLElement;

            let qtyDropup: Dropup | undefined;
            $quantity.addEventListener('focus', e => {
                e.preventDefault();

                const blur = () => {
                    $quantity.blur();
                    $quantity.dispatchEvent(new Event('clearinvalid'));
                };
                blur();
                setTimeout(() => {
                    blur();

                    if (window.window.innerWidth < 768) {
                        qtyDropup = new Dropup({
                            title: 'Select Quantity',
                            body: $radios,
                        });
                    } else {
                        $quantityDropdownMenu.appendChild($radios);
                    }
                });
            });
        });
    } else {
        const $quantityContainer = $body.querySelector('#modal-quantity-container') as HTMLElement;
        $quantityContainer.remove();
        $customQuantityContainer.remove();
    }

    const $dateNeeded = $body.querySelector('#modal-date-needed') as HTMLInputElement;
    $dateNeeded.addEventListener('change', () => {
        $dateNeeded.setCustomValidity('');
        if ($dateNeeded.value) {
            $dateNeededClear.classList.remove('d-none');

            const dateNeeded = DateTime.fromISO($dateNeeded.value);
            if (dateNeeded.isValid && dateNeeded < DateTime.now()) {
                $dateNeeded.setCustomValidity('Please select a future date');
            }
        } else {
            $dateNeededClear.classList.add('d-none');
        }
    });

    const $dateNeededClear = $body.querySelector('#modal-date-needed-clear') as HTMLAnchorElement;
    $dateNeededClear.addEventListener('click', e => {
        e.preventDefault();

        $dateNeeded.value = '';
        $dateNeeded.dispatchEvent(new Event('change'));
    });

    const $comments = $body.querySelector('#modal-comments') as HTMLTextAreaElement;
    if (opts.notesDefault) {
        $comments.value = opts.notesDefault;
    }

    const proofModal = new ModalOrOffcanvas({
        title: 'Get a Proof',
        headerLevel: 'h2',
        body: $body,
        appendClassBySelector: {
            '.offcanvas-body': 'pt-0 px-4',
            '.offcanvas-footer': 'd-none',

            '.modal-dialog': 'px-8',
            '.modal-body': 'px-14',
            '.modal-footer': 'd-none',
        },
        onSubmit: async () => {
            let notes = opts.prefixNotes ?? '';
            if ($comments.value.trim()) {
                if (notes.trim()) {
                    notes += '\n\n';
                }
                notes += $comments.value.trim();
            }

            let quantity: number;
            if (opts.quantity) {
                quantity = opts.quantity;
            } else {
                quantity = $quantity.value === 'custom' ? parseInt($customQuantity.value, 10) : parseInt(CleanNumber($quantity.value), 10);
            }

            const dateNeeded = DateTime.fromISO($dateNeeded.value);

            const quoteRequest = await quoterequests.NewQuoteRequest({
                name: `${$firstName.value} ${$lastName.value}`,
                company: $company.value,
                email: $email.value,
                phone: p,
                quantity,
                sms: true,
                uploader: attachments,
                lineItem: opts.lineItem,
                type: opts.type,
                size: opts.size,
                children: opts.children,
                extras: opts.extras,
                form: opts.form,
                dateNeeded: dateNeeded.isValid ? dateNeeded.toISODate() : undefined,
                notes,
                promo: $promo.value && !promoInput.Dirty ? $promo.value : undefined,
            });

            const sess = await Session().catch(() => undefined);
            const dataLayer = window.dataLayer || [];
            dataLayer.push({
                'event': 'hugo-free-quote-request-form-success',
                'userData': {
                    'email': sess?.email || $email.value,
                    'phone': sess?.phone || await p.Format(),
                    'firstName': sess?.firstName || $firstName.value,
                    'lastName': sess?.lastName || $lastName.value,
                },
                'transactionId': quoteRequest.id,
                'form': opts.form,
            });

            new Modal({
                body: `
                <div class="d-flex flex-column align-items-center text-center">
                    <div class="icon-mark icon-mark-lg mb-6">
                        <span class="material-symbols-rounded">task_alt</span>
                    </div>
                    <h2 class="fw-900 mb-2">Proof Request Sent!</h2>
                    <p class="mb-0">
                        You will receive a proof via email in 1-2 business days.
                    </p>
                </div>`,
                confirmText: opts.backButtonText,
                appendConfirmButtonClass: 'w-100 w-sm-auto btn-sm-lg',
                cancelButton: false,
                appendClassBySelector: {
                    '.modal-dialog': 'proof-request-sent-modal-dialog px-4 px-6',
                    '.modal-body': 'px-6 pt-8 pb-2',
                    '.modal-footer': 'p-6 pt-sm-10 pb-sm-14 justify-content-center',
                },
            });
        },
    });
}
